import { apiClient, handleApiError } from './api'

export const authService = {
  // Login with email and password
  async login(email, password) {
    try {
      const response = await apiClient.post('/api/auth/login', {
        email,
        password
      })
      
      const { access_token, refresh_token, user } = response.data
      
      // Store tokens
      localStorage.setItem('token', access_token)
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token)
      }
      localStorage.setItem('user', JSON.stringify(user))
      
      return { user, token: access_token }
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Register new user - Don't store tokens, user needs to verify email first
  async register(userData) {
    try {
      const response = await apiClient.post('/api/auth/signup', userData)
      
      const { user_id, email, full_name } = response.data
      
      // Don't store tokens - user needs to verify email first
      return {
        user: {
          id: user_id,
          email,
          full_name,
          email_verified: false
        },
        requiresVerification: true
      }
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Logout
  async logout() {
    console.log('authService.logout called')
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      console.log('Refresh token:', refreshToken ? 'found' : 'not found')
      if (refreshToken && refreshToken !== 'null' && refreshToken !== 'undefined') {
        console.log('Making logout API call...')
        await apiClient.post('/api/auth/logout', {
          refresh_token: refreshToken
        })
        console.log('Logout API call completed')
      } else {
        console.log('No valid refresh token, skipping API call')
      }
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API error:', error)
    } finally {
      console.log('Clearing localStorage...')
      // Clear local storage
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      console.log('localStorage cleared')
    }
  },

  // Get current user from token
  async getCurrentUser() {
    try {
      const response = await apiClient.get('/api/auth/me')
      const user = response.data
      
      // Update stored user data
      localStorage.setItem('user', JSON.stringify(user))
      
      return user
    } catch (error) {
      const apiError = handleApiError(error)
      // Add status code to the error for better handling
      apiError.status = error.response?.status
      throw apiError
    }
  },

  // Email verification
  async verifyEmail(token) {
    try {
      const response = await apiClient.post('/api/auth/verify-email', { token })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Resend verification email
  async resendVerificationEmail(email) {
    try {
      const response = await apiClient.post('/api/auth/resend-verification', { email })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Refresh token
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken || refreshToken === 'null' || refreshToken === 'undefined') {
        throw new Error('No refresh token available')
      }

      const response = await apiClient.post('/api/auth/refresh', {
        refresh_token: refreshToken
      })
      
      const { access_token } = response.data
      localStorage.setItem('token', access_token)
      
      return access_token
    } catch (error) {
      // Clear tokens on refresh failure
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      throw handleApiError(error)
    }
  },

  // Google OAuth login
  getGoogleAuthUrl() {
    const baseUrl = import.meta.env.VITE_API_URL
    if (!baseUrl) {
      throw new Error('VITE_API_URL is not defined. Please set it in your .env file.')
    }
    return `${baseUrl}/auth/google`
  },

  // Handle OAuth callback
  async handleOAuthCallback(code, state) {
    try {
      const response = await apiClient.post('/api/auth/google/callback', {
        code,
        state
      })
      
      const { access_token, refresh_token, user } = response.data
      
      // Store tokens
      localStorage.setItem('token', access_token)
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token)
      }
      localStorage.setItem('user', JSON.stringify(user))
      
      return { user, token: access_token }
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Password reset request
  async requestPasswordReset(email) {
    try {
      await apiClient.post('/api/auth/forgot-password', { email })
      return { message: 'Password reset email sent' }
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Reset password with token
  async resetPassword(token, newPassword) {
    try {
      await apiClient.post('/api/auth/reset-password', {
        token,
        new_password: newPassword
      })
      return { message: 'Password reset successful' }
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('token')
    const user = localStorage.getItem('user')
    return !!(token && user)
  },

  // Get stored user data
  getStoredUser() {
    try {
      const userStr = localStorage.getItem('user')
      if (!userStr || userStr === 'undefined' || userStr === 'null') {
        return null
      }
      return JSON.parse(userStr)
    } catch (error) {
      console.error('Error parsing stored user data:', error)
      // Clear invalid data
      localStorage.removeItem('user')
      return null
    }
  },

  // Get stored token
  getStoredToken() {
    return localStorage.getItem('token')
  }
}

export default authService
